import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import { decodeHtml } from "../../utils/decodeHtml";
import { currency_converter_inr } from "../../utils/currencyUtils";

function Collection() {
  const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { setGlobalLoading, token } = useOutletContext();
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const navigate = useNavigate();
  const id = user.id;
  const [data, setData] = useState({});
  const [options, setOptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");

  const financialYears = [
    "2026-2027",
    "2025-2026",
    "2024-2025",
    "2023-2024",
    "2022-2023",
  ];

  const get_collection_data = async () => {
    try {
      setGlobalLoading(true);

      const response = await axios.get(
        `${REACT_APP_API_BASE_URL}/invoices/get_collection_data/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            year: selectedYear,
          },
        }
      );

      if (response.status === 200) {
        const d = response.data || {};

        setData(d);

        setOptions(
          d.collection_invoice_items_list || []
        );
      }
    } catch (error) {
      console.error(
        "Error fetching collection data:",
        error
      );

      setData({});
      setOptions([]);
    } finally {
      setGlobalLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      get_collection_data();
    }
  }, [id, selectedYear]);

  const currentCollection = useMemo(() => {
    return data?.collection_current_year?.[0] || {};
  }, [data]);

  const previousCollection = useMemo(() => {
    return data?.collection_prev_year?.[0] || {};
  }, [data]);

  const clientData = useMemo(() => {
    return data?.collection_client || [];
  }, [data]);

  const memberData = useMemo(() => {
    return data?.collection_member || [];
  }, [data]);


  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 100,

        Cell: ({ row }) => (
          <Link
            to={`/order/view/${row.original.invoice_id}`}
          >
            {decodeHtml(row.original.name || "")}
          </Link>
        ),
      },

      {
        accessorKey: "role_name",
        header: "Role Name",
        size: 120,

        Cell: ({ row }) => (
          <Link
            to={`/role/view/${row.original.role_id}`}
          >
            {decodeHtml(row.original.role_name || "")}
          </Link>
        ),
      },

      {
        accessorKey: "company_name",
        header: "Client",
        size: 120,
      },

      {
        accessorKey: "roleManager",
        header: "Role Manager",
        size: 120,
      },

      {
        accessorKey: "assignedTo",
        header: "Assigned To",
        size: 120,
      },

      {
        accessorKey: "bdBy",
        header: "BD By",
        size: 120,
      },

      {
        accessorKey: "entity",
        header: "Entity",
        size: 80,
      },

      {
        accessorKey: "status",
        header: "Status",
        size: 100,
      },

      {
        accessorKey: "invoice_number",
        header: "Invoice Number",
        size: 120,
      },

      {
        accessorKey: "raised_date",
        header: "Raised On",
        size: 110,
      },

      {
        accessorKey: "issue_date",
        header: "Issued On",
        size: 110,
      },

      {
        accessorKey: "due_date",
        header: "Due Date",
        size: 110,
      },

      {
        accessorKey: "collection_date",
        header: "Collection Date",
        size: 120,
      },

      {
        accessorKey: "major_industry",
        header: "Industry",
        size: 120,
      },

      {
        accessorKey: "region",
        header: "Region",
        size: 100,
      },

      {
        accessorKey: "amount",
        header: "Collection Amount",
        size: 130,
      },

      {
        accessorKey: "revenue",
        header: "Collection (INR)",
        size: 130,

        Cell: ({ row }) =>
          currency_converter_inr(
            row.original.revenue || 0
          ),
      },
    ],
    []
  );

  const exportToExcel = () => {
    const exportData = options.map((row) => ({
      Name: decodeHtml(row.name || ""),

      "Role Name":
        decodeHtml(row.role_name || ""),

      Client: row.company_name || "",

      "Role Manager":
        row.roleManager || "",

      "Assigned To":
        row.assignedTo || "",

      "BD By":
        row.bdBy || "",

      Entity:
        row.entity || "",

      Status:
        row.status || "",

      "Invoice Number":
        row.invoice_number || "",

      "Raised On":
        row.raised_date || "",

      "Issued On":
        row.issue_date || "",

      "Due Date":
        row.due_date || "",

      "Collection Date":
        row.collection_date || "",

      Industry:
        row.major_industry || "",

      Region:
        row.region || "",

      "Collection Amount":
        row.amount || "",

      "Collection (INR)":
        row.revenue || 0,
    }));

    const worksheet =
      XLSX.utils.json_to_sheet(exportData);

    const workbook =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Collection"
    );

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob(
      [excelBuffer],
      {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }
    );

    saveAs(
      file,
      `Collection_${new Date().getTime()}.xlsx`
    );
  };


  const maxClientAmount = useMemo(() => {
    if (!clientData.length) return 1;

    return Math.max(
      ...clientData.map(
        (item) =>
          Number(item.total_amount) || 0
      ),
      1
    );
  }, [clientData]);

  const maxMemberAmount = useMemo(() => {
    if (!memberData.length) return 1;

    return Math.max(
      ...memberData.map(
        (item) =>
          Number(item.total_amount) || 0
      ),
      1
    );
  }, [memberData]);


  return (
    <>
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">

          <div className="my-auto mb-2">
            <h2 className="mb-1">
              Collection
            </h2>

            <div className="text-muted small">
              Collection received against invoices
            </div>
          </div>

          <div className="mb-3 d-flex align-items-center gap-2">

            <button className="btn btn-light btn-sm" onClick={() => navigate("/order/list")}>
              <i className="fa-solid fa-arrow-left me-1"></i>
              Back to Orders
            </button>

            {/* FINANCIAL YEAR */}

            <select className="form-select" style={{ width: "130px" }} value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value="">Till Today</option>

              {financialYears.map((fy) => (
                <option key={fy} value={fy}> {fy} </option>
              ))}
            </select>
          </div>
        </div>

        <div className="card border-0">
          <div className="row">
            <div className="col-md-3 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <h3 className="d-flex fs-17 mb-3" style={{flexDirection: "row"}}>
                    <span className="avatar rounded-circle bg-purple mb-1 me-2">
                      <i className="fa-solid fa-money-bills"></i>
                    </span>

                    <div className="d-flex" style={{flexDirection: "column", justifyContent:"space-evenly"}}>
                      <span className="fw-lighter">
                        Total Collection
                      </span>

                      <span>
                        {currency_converter_inr(currentCollection.revenue || 0 )}
                      </span>
                    </div>
                  </h3>

                  <div className="d-flex align-items-center">
                    <span className="fw-10">
                      Previous Year:{" "}
                      {currency_converter_inr(
                        previousCollection.sum ||
                          0
                      )}
                    </span>

                  </div>

                </div>

              </div>

            </div>

            {/* ============================================= */}
            {/* COLLECTED INVOICES */}
            {/* ============================================= */}

            <div className="col-md-3 d-flex">
              <div className="card flex-fill div-focus-border">
                <div className="card-body">
                  <h3
                    className="d-flex fs-17 mb-3"
                    style={{
                      flexDirection: "row",
                    }}
                  >

                    <span className="avatar rounded-circle bg-pink mb-1 me-2">
                      <i className="fa-solid fa-file-invoice"></i>
                    </span>

                    <div
                      className="d-flex"
                      style={{
                        flexDirection: "column",
                        justifyContent:
                          "space-evenly",
                      }}
                    >

                      <span className="fw-lighter">
                        Collected Invoices
                      </span>

                      <span>
                        {
                          currentCollection.collectionCount ||
                          0
                        }
                      </span>

                    </div>

                  </h3>

                </div>

              </div>

            </div>

            {/* ============================================= */}
            {/* INDIA COLLECTION */}
            {/* ============================================= */}

            <div className="col-md-3 d-flex">

              <div className="card flex-fill">

                <div className="card-body">

                  <h3
                    className="d-flex fs-17 mb-3"
                    style={{
                      flexDirection: "row",
                    }}
                  >

                    <span className="avatar rounded-circle bg-warning mb-1 me-2">
                      <i className="fa-solid fa-indian-rupee-sign"></i>
                    </span>

                    <div
                      className="d-flex"
                      style={{
                        flexDirection: "column",
                        justifyContent:
                          "space-evenly",
                      }}
                    >

                      <span className="fw-lighter">
                        India Collection
                      </span>

                      <span>
                        {
                          currentCollection.india_collection_count ||
                          0
                        }
                      </span>

                    </div>

                  </h3>

                  <div className="d-flex align-items-center">

                    <span className="fw-10">
                      Collection:{" "}
                      {currency_converter_inr(
                        currentCollection.india_revenue ||
                          0
                      )}
                    </span>

                  </div>

                </div>

              </div>

            </div>

            {/* ============================================= */}
            {/* INTERNATIONAL COLLECTION */}
            {/* ============================================= */}

            <div className="col-md-3 d-flex">

              <div className="card flex-fill">

                <div className="card-body">

                  <h3
                    className="d-flex fs-17 mb-3"
                    style={{
                      flexDirection: "row",
                    }}
                  >

                    <span className="avatar rounded-circle bg-secondary mb-1 me-2">
                      <i className="fa-solid fa-plane"></i>
                    </span>

                    <div
                      className="d-flex"
                      style={{
                        flexDirection: "column",
                        justifyContent:
                          "space-evenly",
                      }}
                    >

                      <span className="fw-lighter">
                        International Collection
                      </span>

                      <span>
                        {
                          currentCollection
                            .international_collection_count ||
                          0
                        }
                      </span>

                    </div>

                  </h3>

                  <div className="d-flex align-items-center">

                    <span className="fw-10">
                      Collection:{" "}
                      {currency_converter_inr(
                        currentCollection
                          .international_revenue ||
                          0
                      )}
                    </span>

                  </div>

                </div>

              </div>

            </div>

          </div>

          {/* ================================================= */}
          {/* COLLECTION BREAKDOWN */}
          {/* ================================================= */}

          <div className="row">

            {/* ============================================= */}
            {/* BY CLIENT */}
            {/* ============================================= */}

            <div className="col-md-6 d-flex">

              <div
                className="card flex-fill"
                style={{
                  minHeight: "400px",
                }}
              >

                <div className="card-header pb-2">

                  <h5 className="mb-0">
                    Collection by Client
                  </h5>

                </div>

                <div className="card-body">

                  {clientData.length === 0 ? (

                    <div className="text-center text-muted py-5">
                      No collection data
                    </div>

                  ) : (

                    <div
                      style={{
                        maxHeight: "320px",
                        overflowY: "auto",
                      }}
                    >

                      {clientData.map(
                        (item, index) => {

                          const amount =
                            Number(
                              item.total_amount
                            ) || 0;

                          const percentage =
                            Math.min(
                              100,
                              (amount /
                                maxClientAmount) *
                                100
                            );

                          return (
                            <div
                              key={`${item.client_name}-${index}`}
                              className="mb-3"
                            >

                              <div className="d-flex justify-content-between mb-1">

                                <span
                                  className="text-truncate"
                                  style={{
                                    maxWidth:
                                      "70%",
                                  }}
                                  title={
                                    item.client_name
                                  }
                                >
                                  {
                                    item.client_name
                                  }
                                </span>

                                <span className="fw-semibold">
                                  {amount.toFixed(
                                    2
                                  )} Cr
                                </span>

                              </div>

                              <div
                                className="progress"
                                style={{
                                  height: "8px",
                                }}
                              >

                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  style={{
                                    width: `${percentage}%`,
                                  }}
                                />

                              </div>

                            </div>
                          );
                        }
                      )}

                    </div>

                  )}

                </div>

              </div>

            </div>

            {/* ============================================= */}
            {/* BY TEAM MEMBER */}
            {/* ============================================= */}

            <div className="col-md-6 d-flex">

              <div
                className="card flex-fill"
                style={{
                  minHeight: "400px",
                }}
              >

                <div className="card-header pb-2">

                  <h5 className="mb-0">
                    Collection by Team Member
                  </h5>

                </div>

                <div className="card-body">

                  {memberData.length === 0 ? (

                    <div className="text-center text-muted py-5">
                      No collection data
                    </div>

                  ) : (

                    <div
                      style={{
                        maxHeight: "320px",
                        overflowY: "auto",
                      }}
                    >

                      {memberData.map(
                        (item, index) => {

                          const amount =
                            Number(
                              item.total_amount
                            ) || 0;

                          const percentage =
                            Math.min(
                              100,
                              (amount /
                                maxMemberAmount) *
                                100
                            );

                          return (
                            <div
                              key={`${item.member_name}-${index}`}
                              className="mb-3"
                            >

                              <div className="d-flex justify-content-between mb-1">

                                <span
                                  className="text-truncate"
                                  style={{
                                    maxWidth:
                                      "70%",
                                  }}
                                  title={
                                    item.member_name
                                  }
                                >
                                  {
                                    item.member_name
                                  }
                                </span>

                                <span className="fw-semibold">
                                  {amount.toFixed(
                                    2
                                  )} Cr
                                </span>

                              </div>

                              <div
                                className="progress"
                                style={{
                                  height: "8px",
                                }}
                              >

                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  style={{
                                    width: `${percentage}%`,
                                  }}
                                />

                              </div>

                            </div>
                          );
                        }
                      )}

                    </div>

                  )}

                </div>

              </div>

            </div>

          </div>

          {/* ================================================= */}
          {/* COLLECTION TABLE */}
          {/* ================================================= */}

          <div className="row">

            <div className="card">

              <div className="card-body p-0">

                <div className="custom-datatable-filter table-responsive">

                  {/* EXPORT */}

                  <div className="d-flex justify-content-end mb-1 p-2">

                    <button
                      className="btn btn-sm btn-success"
                      onClick={
                        exportToExcel
                      }
                      disabled={
                        options.length === 0
                      }
                    >
                      <i className="fa fa-file-excel me-2"></i>

                      Export Excel
                    </button>

                  </div>

                  {/* TABLE */}

                  <div className="row p-1 mt-0">

                    {options.length > 0 ? (

                      <MaterialReactTable
                        columns={columns}
                        data={options}

                        enableGrouping

                        enableStickyHeader

                        initialState={{
                          density:
                            "compact",

                          grouping: [
                            "company_name",
                          ],

                          expanded: true,

                          pagination: {
                            pageSize: 100,
                            pageIndex: 0,
                          },

                          columnOrder: [
                            "name",
                            "role_name",
                            "company_name",
                            "roleManager",
                            "assignedTo",
                            "bdBy",
                            "entity",
                            "status",
                            "invoice_number",
                            "raised_date",
                            "issue_date",
                            "due_date",
                            "collection_date",
                            "major_industry",
                            "region",
                            "amount",
                            "revenue",
                          ],

                          columnPinning: {
                            left: [],
                            right: [],
                          },
                        }}

                        muiTableContainerProps={{
                          sx: {
                            maxHeight:
                              "600px",
                          },
                        }}

                        muiTablePaperProps={{
                          elevation: 0,
                        }}
                      />

                    ) : (

                      <div className="text-center text-muted py-5">

                        <i
                          className="fa-solid fa-inbox mb-2"
                          style={{
                            fontSize:
                              "30px",
                          }}
                        ></i>

                        <div>
                          No collection records
                          found for the
                          selected financial
                          year.
                        </div>

                      </div>

                    )}

                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>

      </div>
    </>
  );
}

export default Collection;